/**
 * iter v28 r299 — Badge "Nuovo" sui tab di PropertyDetail.
 *
 * Fetch leggero su `/api/properties/{pid}/tab-activity` (timestamp ultimo
 * cambiamento per tab) → confronto con `localStorage.pronto:lastSeen:{pid}`
 * → ritorna `badges[tab] = true` se è nuovo per l'agente.
 *
 * Polling soft: refetch ogni 60s mentre la pagina è aperta + on visibility
 * change. Niente WebSocket — il dato cambia raramente.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

const STORAGE_PREFIX = "pronto:lastSeen:";

function readSeen(pid) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + pid);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeSeen(pid, obj) {
  try {
    localStorage.setItem(STORAGE_PREFIX + pid, JSON.stringify(obj));
  } catch {
    /* quota error: ignore */
  }
}

export default function useTabBadges(pid, currentTab) {
  const [activity, setActivity] = useState({});
  const [seenMap, setSeenMap] = useState(() => (pid ? readSeen(pid) : {}));

  const fetchActivity = useCallback(async () => {
    if (!pid) return;
    try {
      const { data } = await api.get(`/properties/${pid}/tab-activity`);
      setActivity(data?.activity || {});
    } catch {
      // 404 / 401 → niente badge, fail silent
    }
  }, [pid]);

  // Initial fetch + soft polling + reload on visibility change
  useEffect(() => {
    fetchActivity();
    const timer = setInterval(fetchActivity, 60_000);
    const onVis = () => { if (!document.hidden) fetchActivity(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [fetchActivity]);

  // Mark current tab as seen automatically when it changes
  useEffect(() => {
    if (!pid || !currentTab) return;
    const lastAt = activity[currentTab];
    if (!lastAt) return;
    const seen = readSeen(pid);
    if (seen[currentTab] !== lastAt) {
      const next = { ...seen, [currentTab]: lastAt };
      writeSeen(pid, next);
      setSeenMap(next);
    }
  }, [pid, currentTab, activity]);

  // Compute badges: tab "nuovo" se lastAt > seen[tab]
  const badges = {};
  for (const [k, lastAt] of Object.entries(activity)) {
    if (!lastAt) {
      badges[k] = false;
      continue;
    }
    const seenAt = seenMap[k];
    badges[k] = !seenAt || seenAt < lastAt;
  }

  const markSeen = useCallback(
    (tab) => {
      if (!pid || !tab) return;
      const lastAt = activity[tab];
      if (!lastAt) return;
      const seen = readSeen(pid);
      const next = { ...seen, [tab]: lastAt };
      writeSeen(pid, next);
      setSeenMap(next);
    },
    [pid, activity],
  );

  return { badges, activity, markSeen, refetch: fetchActivity };
}
